import React, { useState } from 'react';
import { isPresent } from 'utils/helperMethods';
import { isEmpty, propEq, find } from 'ramda';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box, TextField, Button, InputAdornment, IconButton, Typography } from '@material-ui/core';
import Select from 'sharedComponents/Select';
import Modal from 'sharedComponents/Modal';
import { validationSchema, initialValues } from 'forms/bidForm';
import Icon from 'sharedComponents/Icon';
import BidPresenter from 'presenters/BidPresenter';

import useStyles from './useStyles';

const INTEREST_RATE_CHANGE_VALUE = 0.125;
const DOLLAR_SIGN = '$';

const BidForm = props => {
  const classes = useStyles();
  const {
    offeringOptions,
    onMakeBid,
    offeringsLoading,
    onChange,
    previousBid,
    onMakeBidOutsideGuidelines,
    currentBidValues,
    bidDisclosure,
  } = props;
  const { t } = useTranslation();
  const [openMakeBidPopup, setOpenMakeBidPopup] = useState(false);
  const [openMakeBidOutsideGuidelinesPopup, setOpenMakeBidOutsideGuidelinesPopup] = useState(false);

  const handleOpenMakeBidPopup = () => setOpenMakeBidPopup(true);
  const handleCloseMakeBidPopup = () => setOpenMakeBidPopup(false);
  const handleOpenMakeBidOutsideGuidelinesPopup = () => setOpenMakeBidOutsideGuidelinesPopup(true);
  const handleCloseMakeBidOutsideGuidelinesPopup = () => setOpenMakeBidOutsideGuidelinesPopup(false);

  const handleMakeBid = () => {
    onMakeBid();
    handleCloseMakeBidPopup();
  };

  const handleMakeBidOutsideGuidelines = () => {
    onMakeBidOutsideGuidelines();
    handleCloseMakeBidPopup();
  };

  const defaultValues = isPresent(currentBidValues) ? currentBidValues : { ...initialValues, notes: bidDisclosure };

  const formik = useFormik({
    validationSchema,
    initialValues: defaultValues,
    onSubmit: handleMakeBid,
  });

  const { errors, values, handleSubmit, isSubmitting, handleBlur, touched, setFieldValue, isValid } = formik;

  const handleClickSubmit = () => {
    handleSubmit();
    handleCloseMakeBidPopup();
  };

  const getOfferingNameById = id => {
    const { label } = find(propEq('value', id.toString()))(offeringOptions);
    return label;
  };

  const isAbleToSubmit = !isSubmitting && isValid;
  const hasPreviousBid = !isEmpty(previousBid);

  const offeringSelectPlaceholder =
    hasPreviousBid && !offeringsLoading
      ? getOfferingNameById(BidPresenter.offeringId(previousBid))
      : t('components.LoanOptions.BidPage.BidForm.selectOfferingPlacheholder');

  const interestRatePlaceholder = hasPreviousBid
    ? BidPresenter.interestRate(previousBid)
    : t('components.LoanOptions.BidPage.BidForm.interestRatePlacheholder');

  const upfrontChargesPlaceholder = hasPreviousBid
    ? BidPresenter.upfrontCharges(previousBid)
    : t('components.LoanOptions.BidPage.BidForm.upfrontChargesPlacheholder');

  const handleValueChange = ({ target: { name, value } }) => {
    onChange(name, Number(value));
    setFieldValue(name, value);
  };

  const handleNotesChange = ({ target: { name, value } }) => {
    onChange(name, value);
    setFieldValue(name, value);
  };

  const handleIncreaseInterestRate = () => {
    if (isEmpty(values.interestRate)) {
      setFieldValue('interestRate', INTEREST_RATE_CHANGE_VALUE.toFixed(3));
      onChange('interestRate', INTEREST_RATE_CHANGE_VALUE);
      return;
    }
    const interestRate = Number.parseFloat(values.interestRate);
    const increasedInterestRate = (interestRate + INTEREST_RATE_CHANGE_VALUE).toFixed(3);
    setFieldValue('interestRate', increasedInterestRate);
    onChange('interestRate', Number.parseFloat(increasedInterestRate));
  };

  const handleDecreaseInterestRate = () => {
    const interestRate = Number.parseFloat(values.interestRate);
    if (!interestRate || interestRate <= INTEREST_RATE_CHANGE_VALUE) {
      setFieldValue('interestRate', (0).toFixed(3));
      onChange('interestRate', 0);
      return;
    }
    const decreasedInterestRate = (interestRate - INTEREST_RATE_CHANGE_VALUE).toFixed(3);
    setFieldValue('interestRate', decreasedInterestRate);
    onChange('interestRate', Number.parseFloat(decreasedInterestRate));
  };

  return (
    <Box className={classes.container}>
      <Box mt={2}>
        <Box width="100%" display="flex" height={100}>
          <Box width="70%">
            <Typography variant="subtitle1" color="secondary" className={classes.fieldLabel}>
              {t('components.LoanOptions.BidPage.BidForm.productSelection')}
            </Typography>
            <Select
              name="offeringId"
              id="offeringId"
              className={classes.offeringSelect}
              fullWidth
              placeholder={offeringSelectPlaceholder}
              disabled={offeringsLoading}
              options={offeringOptions}
              onChange={handleValueChange}
              defaultOptionValue={values.offeringId}
              error={touched.offeringId && Boolean(errors.offeringId)}
              helperText={touched.offeringId && t(errors.offeringId)}
            />
          </Box>
          <Box ml={1} width="30%" minWidth={130}>
            <Typography variant="subtitle1" color="secondary" className={classes.fieldLabel}>
              {t('components.LoanOptions.BidPage.BidForm.interestRate')}
            </Typography>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton onClick={handleDecreaseInterestRate} className={classes.iconButton}>
                      <Icon name="minus" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleIncreaseInterestRate} className={classes.iconButton}>
                      <Icon name="plus" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder={interestRatePlaceholder}
              name="interestRate"
              id="interestRate"
              onChange={handleValueChange}
              variant="outlined"
              onBlur={handleBlur}
              className={classes.field}
              value={values.interestRate}
              error={touched.interestRate && Boolean(errors.interestRate)}
              helperText={touched.interestRate && t(errors.interestRate)}
            />
          </Box>
        </Box>
        <Box width="100%" mb={2}>
          <Typography variant="subtitle1" color="secondary" className={classes.fieldLabel}>
            {t('components.LoanOptions.BidPage.BidForm.upfrontCharges')}
          </Typography>
          <TextField
            InputProps={{
              startAdornment: <InputAdornment position="start">{DOLLAR_SIGN}</InputAdornment>,
            }}
            name="upfrontCharges"
            id="upfrontCharges"
            placeholder={upfrontChargesPlaceholder}
            onChange={handleValueChange}
            variant="outlined"
            onBlur={handleBlur}
            className={classes.field}
            value={values.upfrontCharges}
            error={touched.upfrontCharges && Boolean(errors.upfrontCharges)}
            helperText={touched.upfrontCharges && t(errors.upfrontCharges)}
          />
        </Box>
        <Box width="100%" mb={2}>
          <Typography variant="subtitle1" color="secondary" className={classes.fieldLabel}>
            {t('components.LoanOptions.BidPage.BidForm.notes')}
          </Typography>
          <TextField
            name="notes"
            id="notes"
            multiline
            rows={4}
            onChange={handleNotesChange}
            variant="outlined"
            onBlur={handleBlur}
            className={classes.field}
            value={values.notes}
            error={touched.notes && Boolean(errors.notes)}
            helperText={touched.notes && t(errors.notes)}
          />
        </Box>
      </Box>
      <Box display="flex">
        <Button onClick={handleOpenMakeBidPopup} disabled={!isAbleToSubmit}>
          {hasPreviousBid
            ? t('components.LoanOptions.BidPage.BidForm.updateBidButton')
            : t('components.LoanOptions.BidPage.BidForm.makeBidButton')}
        </Button>
        <Button className={classes.marginLeft} onClick={handleOpenMakeBidOutsideGuidelinesPopup} color="secondary">
          {t('components.LoanOptions.BidPage.BidForm.makeBidOutsideGuidelinesButton')}
        </Button>
      </Box>
      {openMakeBidOutsideGuidelinesPopup && (
        <Modal onClose={handleCloseMakeBidOutsideGuidelinesPopup} closeButton>
          <Box width={400} p={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography variant="h2">
              {t('components.LoanOptions.BidPage.BidForm.makeBidRequestOutsideGuidelinesApprove')}
            </Typography>
            <Box mt={3} display="flex" width={150} justifyContent="space-between">
              <Button onClick={handleMakeBidOutsideGuidelines}>
                {t('components.LoanOptions.BidPage.BidForm.yesButtonText')}
              </Button>
              <Button onClick={handleCloseMakeBidOutsideGuidelinesPopup} color="secondary">
                {t('components.LoanOptions.BidPage.BidForm.noButtonText')}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      {openMakeBidPopup && (
        <Modal onClose={handleCloseMakeBidPopup} closeButton>
          <Box width={400} p={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography variant="h2">{t('components.LoanOptions.BidPage.BidForm.makeBidRequestApprove')}</Typography>
            <Box mt={3} display="flex" width={150} justifyContent="space-between">
              <Button onClick={handleClickSubmit}>{t('components.LoanOptions.BidPage.BidForm.yesButtonText')}</Button>
              <Button onClick={handleCloseMakeBidPopup} color="secondary">
                {t('components.LoanOptions.BidPage.BidForm.noButtonText')}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

BidForm.propTypes = {
  offeringOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  offeringsLoading: PropTypes.bool.isRequired,
  previousBid: BidPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  onMakeBid: PropTypes.func.isRequired,
  bidDisclosure: PropTypes.string.isRequired,
  onMakeBidOutsideGuidelines: PropTypes.func.isRequired,
  currentBidValues: PropTypes.shape(),
};

BidForm.defaultProps = {
  currentBidValues: null,
};

export default BidForm;



// WEBPACK FOOTER //
// ./src/sharedComponents/LoanOptions/components/BidPage/components/BidForm/BidForm.js